// @ts-nocheck
import React, { useState, useMemo } from 'react';
import { Student, Homework, Submission, Assignment } from '../../types';
import { XMarkIcon, TrophyIcon, ClipboardDocumentCheckIcon, ClockIcon, ChartBarIcon, ChevronDownIcon } from './Icons';
import Card from './Card';
import { isSubmissionGraded, isSubmissionSubmitted, getStatusDisplay } from '../../utils/assignmentUtils';

interface StudentReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    student: Student;
    homeworks: Homework[];
    submissions: Submission[];
    assignments: Assignment[];
}

const StudentReportModal: React.FC<StudentReportModalProps> = ({ isOpen, onClose, student, homeworks, submissions, assignments }) => {
    const [selectedSemester, setSelectedSemester] = useState<'Học kỳ 1' | 'Học kỳ 2' | 'Cả năm'>('Học kỳ 1');
    const [selectedCategory, setSelectedCategory] = useState<'Tất cả' | 'Luyện tập' | 'Kiểm tra' | 'Dự án'>('Tất cả');

    const studentReports = useMemo(() => {
        // Filter submissions for this student
        const studentSubmissions = submissions.filter(s => s.studentId === student.email || s.studentId === student.id);
        
        return studentSubmissions.map(sub => {
            const assignment = assignments.find(a => a.id === sub.assignmentId);
            const homework = homeworks.find(h => h.id === (assignment?.homeworkId));
            
            // Mock category and semester logic based on date or title
            const date = assignment?.assignedDate ? new Date(assignment.assignedDate) : new Date();
            const semester = date.getMonth() < 6 ? 'Học kỳ 2' : 'Học kỳ 1';
            const category = homework?.title.toLowerCase().includes('kiểm tra') ? 'Kiểm tra' : 
                             homework?.title.toLowerCase().includes('dự án') ? 'Dự án' : 'Luyện tập';

            return {
                id: sub.id,
                title: homework?.title || 'Nhiệm vụ không xác định',
                score: sub.grade || 0,
                status: sub.status,
                date: assignment?.assignedDate || '---',
                semester,
                category,
                comment: sub.teacherComments
            };
        });
    }, [student, submissions, homeworks, assignments]);

    const filteredReports = studentReports.filter(r => {
        const semesterMatch = selectedSemester === 'Cả năm' || r.semester === selectedSemester;
        const categoryMatch = selectedCategory === 'Tất cả' || r.category === selectedCategory;
        return semesterMatch && categoryMatch;
    });

    const stats = useMemo(() => {
        if (filteredReports.length === 0) return { avg: 0, completed: 0, total: 0 };
        const graded = filteredReports.filter(r => isSubmissionGraded(r.status));
        const avg = graded.length > 0 ? graded.reduce((acc, curr) => acc + curr.score, 0) / graded.length : 0;
        return {
            avg: avg.toFixed(1),
            completed: filteredReports.filter(r => isSubmissionSubmitted(r.status)).length,
            total: filteredReports.length
        };
    }, [filteredReports]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto py-8" onClick={onClose}>
            <div className="bg-slate-50 rounded-2xl shadow-2xl max-w-4xl w-full m-4 flex flex-col max-h-full overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-6 bg-white border-b flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-xl font-black">
                            {student.name.charAt(0)}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-slate-900">{student.name}</h3>
                            <p className="text-xs text-slate-500 font-medium">Mã HS: {student.studentId || '---'} • {student.dob || '---'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 transition-colors">
                        <XMarkIcon className="h-6 w-6 text-slate-400" />
                    </button>
                </div>

                {/* Filters & Stats */}
                <div className="p-6 space-y-6 overflow-y-auto flex-grow">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="p-4 bg-white border-none shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-teal-50 text-teal-600 rounded-xl">
                                <TrophyIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Điểm trung bình</p>
                                <p className="text-2xl font-black text-teal-600">{stats.avg}</p>
                            </div>
                        </Card>
                        <Card className="p-4 bg-white border-none shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                                <ClipboardDocumentCheckIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Hoàn thành</p>
                                <p className="text-2xl font-black text-blue-600">{stats.completed}/{stats.total}</p>
                            </div>
                        </Card>
                        <Card className="p-4 bg-white border-none shadow-sm flex items-center gap-4">
                            <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                                <ChartBarIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Xếp loại</p>
                                <p className="text-2xl font-black text-purple-600">
                                    {stats.avg >= 9 ? 'Xuất sắc' : stats.avg >= 8 ? 'Giỏi' : stats.avg >= 6.5 ? 'Khá' : stats.avg >= 5 ? 'TB' : 'Yếu'}
                                </p>
                            </div>
                        </Card>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                        <div className="flex gap-2">
                            {['Học kỳ 1', 'Học kỳ 2', 'Cả năm'].map(sem => (
                                <button 
                                    key={sem}
                                    onClick={() => setSelectedSemester(sem as any)}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${selectedSemester === sem ? 'bg-teal-600 text-white shadow-md shadow-teal-100' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                                >
                                    {sem}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-2">
                            {['Tất cả', 'Luyện tập', 'Kiểm tra', 'Dự án'].map(cat => (
                                <button 
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat as any)}
                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${selectedCategory === cat ? 'bg-sky-600 text-white shadow-md shadow-sky-100' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Report List */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                            <ClipboardDocumentCheckIcon className="h-4 w-4" /> Chi tiết bài làm & Điểm số
                        </h4>
                        
                        {filteredReports.length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed">
                                <p className="text-gray-400 text-sm">Không có dữ liệu trong bộ lọc này.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-3">
                                {filteredReports.map(report => (
                                    <div key={report.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm hover:border-teal-200 transition-all group">
                                        <div className="flex justify-between items-start gap-4">
                                            <div className="flex-grow">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                                        report.category === 'Kiểm tra' ? 'bg-red-50 text-red-600' :
                                                        report.category === 'Dự án' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
                                                    }`}>
                                                        {report.category}
                                                    </span>
                                                    <span className="text-[10px] font-bold text-gray-400 flex items-center gap-1">
                                                        <ClockIcon className="h-3 w-3" /> {report.date}
                                                    </span>
                                                </div>
                                                <h5 className="font-bold text-gray-800 group-hover:text-teal-600 transition-colors">{report.title}</h5>
                                                {report.comment && (
                                                    <p className="mt-2 text-xs text-gray-500 italic bg-slate-50 p-2 rounded-lg border-l-2 border-slate-300">
                                                        " {report.comment} "
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className={`text-2xl font-black ${
                                                    report.score >= 8 ? 'text-green-600' : report.score >= 5 ? 'text-amber-600' : 'text-red-600'
                                                }`}>
                                                    {isSubmissionGraded(report.status) ? report.score : '---'}
                                                </div>
                                                <p className={`text-[10px] font-bold uppercase tracking-tighter ${
                                                    isSubmissionGraded(report.status) ? 'text-green-500' : 'text-gray-400'
                                                }`}>
                                                    {getStatusDisplay(report.status)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white px-6 py-4 flex justify-between items-center border-t flex-shrink-0">
                    <p className="text-xs text-gray-400 italic font-medium">* Dữ liệu được tổng hợp tự động từ kết quả bài tập & kiểm tra trực tuyến.</p>
                    <button onClick={onClose} className="px-8 py-2 bg-gray-900 text-white text-sm font-black rounded-xl hover:bg-gray-800 transition-all shadow-lg active:scale-95">Đóng báo cáo</button>
                </div>
            </div>
        </div>
    );
};

export default StudentReportModal;
